/* globals $ */
import { registerClass } from 'libs/ComponentLoader'
import { handleize } from '_global/js/helpers'
import debounce from 'debounce'

import {
  search
} from 'libs/rcmSearchApi'

import './styles.scss'

export const CLOSE_SEARCH = 'SearchInput:close_search'

function sortByCount (a, b) {
  return b.count - a.count
}

class SearchInput {
  constructor (element) {
    this.$el = $(element)

    this.lastSearch = ''
    this.focusTimeout = null

    this.$input = this.$el.find('.searchInput-input')
    this.$searchButton = this.$el.find('.searchInput-searchButton')
    this.$closeButton = this.$el.find('.searchInput-closeButton')
    this.$resultsList = this.$el.find('.searchInput-resultsList')
    this.$resultDescription = this.$el.find('.searchInput-description')
    this.$listItemTemplate = $(this.$el.find('.searchInput-listItemTemplate').html())
    this.$formButtons = this.$el.find('button')
    this.$formInputs = this.$el.find('input')
    this.$parent = this.$el.parent()

    this.externalSearchUrl = this.$el.attr('data-external-url')
    this.storeSearchUrl = this.$el.attr('action')
    this.isStandalone = this.$el.attr('data-standalone') === 'true'
    this.originalValue = this.$input.val()

    this.parseResults = this.parseResults.bind(this)
    this.sendSearch = debounce(this.sendSearch.bind(this), 100)

    if (!this.$parent.hasClass('header-searchDropdownWrapper')) {
      this.addToTabOrder(this.$el)
    }
    this.$closeButton.on('click', this.close.bind(this))
    this.$input.on('keyup', this.keyUpHandler.bind(this))
    this.$el.on('submit', this.onSubmit.bind(this))
    this.$el.on('focusout', (e) => {
      if (this.focusTimeout) {
        clearTimeout(this.focusTimeout)
      }
      this.focusTimeout = setTimeout(() => {
        this.close()
      }, 300)

      if (this.$parent.hasClass('header-searchDropdownWrapper')) this.removeFromTabOrder(this.$el)
    })
    this.$el.on('focusin', (e) => {
      if (this.focusTimeout) {
        clearTimeout(this.focusTimeout)
      }

      this.addToTabOrder(this.$el)
    })
  }

  open () {
    this.$input.focus()
  }

  close () {
    this.lastSearch = ''
    this.$resultsList.html('')
    this.$el.removeClass('searchInput-hasTerm')
    this.$el.addClass('searchInput-noTerm')
    this.$resultDescription.attr('aria-live', 'off').text('')

    if (this.isStandalone) {
      this.$input.val(this.originalValue)
    } else {
      this.$input.val('')
      this.$el.trigger(CLOSE_SEARCH)
    }
  }

  keyUpHandler (e) {
    if (e.which === 27) {
      this.close()
    } else {
      this.sendSearch()
    }
  }

  onSubmit (e) {
    e.preventDefault()

    const searchTarget = this.$el.find('input[type=radio][name=searchTarget]:checked').val()
    const term = this.$input.val()

    switch (searchTarget) {
      case 'store':
        window.location = `${this.storeSearchUrl}?q=${term}`
        break

      case 'external':
        window.open(this.externalSearchUrl.replace('[searchterm]', term), '_blank')
        break
    }
  }

  sendSearch () {
    if (this.$input.val() === this.lastSearch) {
      return
    }
    const searchTerm = this.$input.val()
    if (searchTerm) {
      search.quickSearch(this.$input.val()).then(this.parseResults)

      if (this.$el.hasClass('searchInput-noTerm')) {
        this.$el.removeClass('searchInput-noTerm')
        this.$el.addClass('searchInput-hasTerm')
        this.$resultDescription.attr('aria-live', 'assertive')
          .text(`${this.$resultDescription.data('description')}`)
      }
    } else {
      if (this.$el.hasClass('searchInput-hasTerm')) {
        this.$el.removeClass('searchInput-hasTerm')
        this.$el.addClass('searchInput-noTerm')
        this.$resultDescription.attr('aria-live', 'off')
          .text('')
      }
    }
    this.lastSearch = searchTerm
  }

  parseResults (response) {
    const {
      facets,
      suggestions
    } = response

    const MAX_COLLECTIONS = 2
    const MAX_SUGGESTIONS = 2
    let max = 0
    let items = []

    // first item is just the search term straight-up
    items.push(this.renderListItem({
      term: this.lastSearch
    }))

    // find the collections with most results.
    for (let i = 0, len = facets.length; i < len; i++) {
      const facet = facets[i]
      if (facet.attribute === 'collections') {
        const buckets = facet.buckets.sort(sortByCount)
        max = MAX_COLLECTIONS < buckets.length ? MAX_COLLECTIONS : buckets.length
        for (let ii = 0; ii < max; ii++) {
          const collection = buckets[ii]

          items.push(this.renderListItem({
            term: this.lastSearch,
            collectionName: collection.value,
            collectionHandle: handleize(collection.value)
          }))
        }
        break
      }
    }

    // get search suggestions
    max = MAX_SUGGESTIONS < suggestions.length ? MAX_COLLECTIONS : suggestions.length
    for (let i = 0; i < max; i++) {
      const suggestion = suggestions[i]
      if (suggestion !== this.lastSearch) {
        items.push(this.renderListItem({
          term: this.lastSearch,
          suggestion: suggestion
        }))
      }
    }

    this.$resultsList.html('')
    this.$resultsList.append(items)
  }

  renderListItem (data) {
    const {
      term,
      collectionName,
      collectionHandle,
      suggestion
    } = data

    const $newItem = this.$listItemTemplate.clone()
    let itemHTML = $newItem.html()
    let searchUrl = `/search?q=${term}`

    itemHTML = itemHTML.replace('[searchterm]', term)
    $newItem.html(itemHTML)

    if (collectionName) {
      $newItem.find('a span').html(`in ${collectionName}`)
      $newItem.addClass('searchInput-result-collection')
      searchUrl = `/collections/${collectionHandle}?q=${term}`
    } else if (suggestion) {
      const suggestionRemainder = suggestion.toLowerCase().replace(term.toLowerCase(), '')
      $newItem.find('a span').html(suggestionRemainder)
      $newItem.addClass('searchInput-result-suggestion')
      searchUrl = `/search?q=${suggestion}`
    }

    $newItem.find('a').attr('href', searchUrl)

    return $newItem
  }

  removeFromTabOrder ($form) {
    const $buttons = $form.find('button')
    const $inputs = $form.find('input')

    $form.attr('tabindex', '-1').attr('aria-hidden', 'true')
    $buttons.attr('tabindex', '-1')
    $inputs.attr('tabindex', '-1')
  }

  addToTabOrder ($form) {
    const $buttons = $form.find('button')
    const $inputs = $form.find('input')

    $form.attr('tabindex', '0').attr('aria-hidden', 'false')
    $buttons.attr('tabindex', '0')
    $inputs.attr('tabindex', '0')
  }
}

registerClass('SearchInput', SearchInput)
